import { createContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  studyPhaseNavigator,
  setNavigateFunction,
} from './studyPhaseNavigator';
import { useSelector } from 'react-redux';
import {
  selectStudyPhase,
  StudyPhase,
  isLoadingPhase,
} from '../slices/study/studySlice';
import { selectActiveBlock } from '../slices/blocks/blocksSlice';

export const StudyActorContext = createContext(studyPhaseNavigator);

// Helper function to get the base phase from a loading phase
const getBasePhase = (phase: StudyPhase): StudyPhase => {
  if (!isLoadingPhase(phase)) return phase;

  switch (phase) {
    case StudyPhase.LOADING_BLOCK_SELECT:
      return StudyPhase.BLOCK_SELECT;
    case StudyPhase.LOADING_BLOCK_STUDY:
      return StudyPhase.BLOCK_STUDY;
    case StudyPhase.LOADING_BLOCK_REVIEW:
      return StudyPhase.BLOCK_REVIEW;
    case StudyPhase.LOADING_FLASHCARD_STUDY:
      return StudyPhase.FLASHCARD_STUDY;
    case StudyPhase.LOADING_SESSION_SUMMARY:
      return StudyPhase.SESSION_SUMMARY;
    case StudyPhase.LOADING_HOME:
      return StudyPhase.HOME;
    case StudyPhase.LOADING_VIDEO_STUDY:
      return StudyPhase.VIDEO_STUDY;
    default:
      return phase;
  }
};

// Reverse mapping from routes to study phases
const routeToStateMap: Record<string, StudyPhase> = {
  '/': StudyPhase.HOME,
  '/study/qbank': StudyPhase.BLOCK_SELECT,
  '/study/qbank/block': StudyPhase.BLOCK_STUDY,
  '/study/qbank/block/review': StudyPhase.BLOCK_REVIEW,
  '/study/flashcards': StudyPhase.FLASHCARD_STUDY,
  '/study/summary': StudyPhase.SESSION_SUMMARY,
  '/study/video': StudyPhase.VIDEO_STUDY,
};

export const StudyPhaseProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentStudyPhase = useSelector(selectStudyPhase);
  const activeBlock = useSelector(selectActiveBlock);

  useEffect(() => {
    // Set the navigate function so the study service can use it
    setNavigateFunction(navigate);
  }, [navigate]);

  useEffect(() => {
    // Sync machine state with current route on direct navigation
    const expectedState = routeToStateMap[location.pathname];

    if (location.pathname === '/study') {
      studyPhaseNavigator.send({ type: 'RETURN_TO_HOME' });
      return;
    }

    // Compare base phases to handle loading states properly
    const currentBasePhase = getBasePhase(currentStudyPhase);
    const expectedBasePhase = expectedState
      ? getBasePhase(expectedState)
      : null;

    if (expectedBasePhase && expectedBasePhase !== currentBasePhase) {
      switch (expectedBasePhase) {
        case StudyPhase.HOME:
          studyPhaseNavigator.send({ type: 'RETURN_TO_HOME' });
          break;
        case StudyPhase.BLOCK_STUDY:
        case StudyPhase.BLOCK_SELECT:
          studyPhaseNavigator.send({ type: 'START_QBANK' });
          break;
        case StudyPhase.BLOCK_REVIEW:
          studyPhaseNavigator.send({
            type: 'REVIEW_QBANK',
            blockId: activeBlock?.id,
          });
          break;
        case StudyPhase.FLASHCARD_STUDY:
          studyPhaseNavigator.send({ type: 'START_FLASHCARDS' });
          break;
        case StudyPhase.SESSION_SUMMARY:
          studyPhaseNavigator.send({ type: 'START_SESSION_GENERIC' });
          break;
        default:
          break;
      }
    }
  }, [location.pathname]);

  return (
    <StudyActorContext.Provider value={studyPhaseNavigator}>
      {children}
    </StudyActorContext.Provider>
  );
};
